// Отложенные глобальные слоты корневого маршрута.
//
// Зачем файл существует. Всё, что смонтировано в `__root.tsx`, попадает в
// СТАТИЧЕСКИЙ граф входного чанка: браузер обязан скачать и исполнить его до
// первого экрана, а сборка вдобавок печатает в `index.html` по одному
// `<link rel="modulepreload">` на каждый файл графа. Замер прода 2026-09-03:
// 306 таких ссылок, 307 файлов, 3,64 МБ в сыром виде — и это гостю на лендинге.
// Внутри лежал весь репетитор с KaTeX (250 КБ) — цепочкой
// index → suggested-replies → katex-options → normalize-latex → katex.
// Chrome отвечал на это предупреждением «was preloaded ... but not used» на
// каждый неиспользованный файл: тысячи строк в консоли, в которых тонет всё
// остальное.
//
// Правило: глобальный слот, который открывается ДЕЙСТВИЕМ (кнопка, хоткей,
// вызов стора), не имеет права лежать в стартовом графе. Он объявляется здесь
// через `lazy()` и монтируется, только когда свой стор впервые сказал
// «открыто».
//
// Защёлка (`useLatch`) обязательна. Если снимать компонент при закрытии, у
// диалога обрывается анимация выхода и теряется внутреннее состояние — то есть
// экономия оплачивалась бы поведением. Открыли один раз — слот остаётся
// смонтированным до ухода со страницы, дальше всё как раньше.
//
// Что сюда НЕ переносить: слоты, которые решают показаться САМИ (баннеры,
// listeners, `PromoGrantedDialog` с собственным запросом), и всё, что участвует
// в первом экране. Отложить их — не ускорить старт, а получить мигание.

import { toast } from "@studyhub/ui/toast";
import { lazy, type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChunkSafe } from "@/components/shared/lazy-art";
import { useAuthExpiredStore } from "@/lib/auth-expired-store";
import { useCommerceVisible } from "@/lib/module-availability/commerce";
import { useAddMenuStore } from "@/stores/add-menu";
import { useCommandPaletteStore } from "@/stores/command-palette";
import { useCourseCreatePathStore } from "@/stores/course-create-path";
import { useDiscoverDialogStore } from "@/stores/discover-dialog";
import { useHowItWorksStore } from "@/stores/how-it-works";
import { useLoginModalStore } from "@/stores/login-modal";
import { useSidePanelStore } from "@/stores/side-panel";
import { useSubscriptionModalStore } from "@/stores/subscription-modal";
import { useUpgradeModalStore } from "@/stores/upgrade-modal";
import { useUploadDialogStore } from "@/stores/upload-dialog";

/**
 * Защёлка: как только условие один раз стало истинным, остаётся истинным.
 * `latched || active` нужен для того же рендера, в котором условие сработало, —
 * иначе слот появился бы кадром позже, и открытие диалога подмигивало бы.
 */
function useLatch(active: boolean): boolean {
	const [latched, setLatched] = useState(active);
	if (active && !latched) setLatched(true);
	return latched || active;
}

/** Слот: ничего до первого открытия, дальше — ленивый чанк под своей границей. */
function Slot({ when, children }: { when: boolean; children: ReactElement }): ReactElement | null {
	const mounted = useLatch(when);
	if (!mounted) return null;
	// fallback = null: глобальный слот невидим, пока не открыт, и его сбой не
	// имеет права нарисовать что-либо поверх страницы.
	return (
		<ChunkSafe fallback={null} source="deferred-global">
			{children}
		</ChunkSafe>
	);
}

const TutorSidePanelLazy = lazy(() =>
	import("@/components/layout/tutor-side-panel").then((m) => ({ default: m.TutorSidePanel })),
);
const GlobalCommandPaletteLazy = lazy(() =>
	import("@/components/command-palette/global-command-palette").then((m) => ({
		default: m.GlobalCommandPalette,
	})),
);
const UpgradeModalLazy = lazy(() =>
	import("@/components/billing/dialogs/upgrade-modal").then((m) => ({ default: m.UpgradeModal })),
);
const ManageSubscriptionDialogLazy = lazy(() =>
	import("@/components/billing/dialogs/manage-subscription-dialog").then((m) => ({
		default: m.ManageSubscriptionDialog,
	})),
);
const UploadDialogLazy = lazy(() =>
	import("@/components/materials/upload-dialog").then((m) => ({ default: m.UploadDialog })),
);
const AddMenuDialogLazy = lazy(() =>
	import("@/components/layout/add-menu-dialog").then((m) => ({ default: m.AddMenuDialog })),
);
const GlobalDiscoverDialogLazy = lazy(() =>
	import("@/components/discover/global-discover-dialog").then((m) => ({
		default: m.GlobalDiscoverDialog,
	})),
);
const HowItWorksDialogLazy = lazy(() =>
	import("@/components/how-it-works/how-it-works-dialog").then((m) => ({
		default: m.HowItWorksDialog,
	})),
);
const LoginModalLazy = lazy(() =>
	import("@/components/auth/login-modal").then((m) => ({ default: m.LoginModal })),
);
const AuthExpiredModalLazy = lazy(() =>
	import("@/components/auth/AuthExpiredModal").then((m) => ({ default: m.AuthExpiredModal })),
);

/**
 * AI-репетитор: единый глобальный инстанс панели.
 *
 * `suppressed` гасит слот жёстко, а не через защёлку: на шлюзовых маршрутах
 * (согласие, онбординг, чекаут) панель обязана быть СНЯТА, иначе она фоном
 * дёргает `tutor.sessions.*` там, где пользователь ещё не дошёл до приложения.
 */
export function DeferredTutorSidePanel({
	suppressed,
}: {
	suppressed: boolean;
}): ReactElement | null {
	const open = useSidePanelStore((s) => s.open && s.kind === "tutor");
	const mounted = useLatch(open);
	if (suppressed || !mounted) return null;
	return (
		<ChunkSafe fallback={null} source="deferred-global">
			<TutorSidePanelLazy />
		</ChunkSafe>
	);
}

export function DeferredCommandPalette(): ReactElement | null {
	const isOpen = useCommandPaletteStore((s) => s.isOpen);
	return (
		<Slot when={isOpen}>
			<GlobalCommandPaletteLazy />
		</Slot>
	);
}

/**
 * Модалка апгрейда — витрина с перечнем преимуществ уровня, ценой и кнопкой
 * оплаты. При скрытой продаже её не должно быть: App Store 3.1.1 запрещает
 * показывать подписку, пока её нельзя купить через IAP. Сборку 1.0 (3)
 * завернули в том числе на этом.
 *
 * ── Почему решение здесь, а не в сторе ─────────────────────────────────────
 *
 * Точек открытия у витрины 127 (`openUpgrade` / `openUpgradeOnQuota`), и гейт
 * в каждой не удержится — сто первая забудется. Напрашивается проверка внутри
 * `open()`, но стор обязан оставаться чистым (гейт `web-stores-are-pure`), а
 * решение требует и признака доступности, и словаря для сообщения.
 *
 * Слот подходит лучше стора: через него проходят ВСЕ открытия, он смонтирован
 * в корне один раз, и ему по слою положено знать и про `lib/`, и про i18n.
 *
 * Молча гасить нельзя — человек нажал кнопку и обязан получить ответ. Ответ
 * говорит о ФАКТЕ («лимит исчерпан», «недоступно»), а не о его причине в
 * терминах тарифов: упоминание уровня вернуло бы ровно то, что мы прячем.
 */
export function DeferredUpgradeModal(): ReactElement | null {
	const isOpen = useUpgradeModalStore((s) => s.isOpen);
	const trigger = useUpgradeModalStore((s) => s.trigger);
	const close = useUpgradeModalStore((s) => s.close);
	const commerceVisible = useCommerceVisible();
	const { t } = useTranslation("billing");

	useEffect(() => {
		if (!isOpen || commerceVisible) return;
		toast.info(
			trigger?.kind === "quota-exceeded"
				? t("noSale.quotaReached")
				: t("noSale.featureUnavailable"),
		);
		close();
	}, [isOpen, commerceVisible, trigger, close, t]);

	return (
		<Slot when={isOpen && commerceVisible}>
			<UpgradeModalLazy />
		</Slot>
	);
}

/** Управление подпиской — то же правило: продажи нет, значит и управления нет. */
export function DeferredManageSubscriptionDialog(): ReactElement | null {
	const isOpen = useSubscriptionModalStore((s) => s.isOpen);
	const commerceVisible = useCommerceVisible();
	return (
		<Slot when={isOpen && commerceVisible}>
			<ManageSubscriptionDialogLazy />
		</Slot>
	);
}

export function DeferredUploadDialog(): ReactElement | null {
	const isOpen = useUploadDialogStore((s) => s.isOpen);
	const setOpen = useUploadDialogStore((s) => s.setOpen);
	const targetSetId = useUploadDialogStore((s) => s.targetSetId);
	const targetCourseId = useUploadDialogStore((s) => s.targetCourseId);
	const initialFolderId = useUploadDialogStore((s) => s.initialFolderId);
	return (
		<Slot when={isOpen}>
			<UploadDialogLazy
				open={isOpen}
				onOpenChange={setOpen}
				targetSetId={targetSetId ?? undefined}
				targetCourseId={targetCourseId ?? undefined}
				initialFolderId={initialFolderId ?? undefined}
			/>
		</Slot>
	);
}

/**
 * Меню «+».
 *
 * Условие монтирования ДВОЙНОЕ, и это не перестраховка. `AddMenuDialog` рядом
 * с собой рендерит единственный на приложение экземпляр развилки создания
 * курса (`CourseCreatePathDialog`), а открывают её ещё из трёх мест: чек-лист
 * первых шагов на дашборде, страница `/courses` и справка «Как это работает».
 * Пока меню висело в корне всегда, это работало само. Если оставить защёлку
 * только на своём сторе, развилка из этих трёх точек не откроется вообще:
 * стор скажет «открыто», а рендерить её будет некому.
 */
export function DeferredAddMenuDialog(): ReactElement | null {
	const isOpen = useAddMenuStore((s) => s.isOpen);
	const coursePathOpen = useCourseCreatePathStore((s) => s.isOpen);
	return (
		<Slot when={isOpen || coursePathOpen}>
			<AddMenuDialogLazy />
		</Slot>
	);
}

export function DeferredDiscoverDialog(): ReactElement | null {
	const isOpen = useDiscoverDialogStore((s) => s.isOpen);
	return (
		<Slot when={isOpen}>
			<GlobalDiscoverDialogLazy />
		</Slot>
	);
}

export function DeferredHowItWorksDialog(): ReactElement | null {
	const isOpen = useHowItWorksStore((s) => s.isOpen);
	return (
		<Slot when={isOpen}>
			<HowItWorksDialogLazy />
		</Slot>
	);
}

export function DeferredLoginModal(): ReactElement | null {
	const isOpen = useLoginModalStore((s) => s.isOpen);
	return (
		<Slot when={isOpen}>
			<LoginModalLazy />
		</Slot>
	);
}

export function DeferredAuthExpiredModal(): ReactElement | null {
	const isOpen = useAuthExpiredStore((s) => s.isOpen);
	return (
		<Slot when={isOpen}>
			<AuthExpiredModalLazy />
		</Slot>
	);
}

/**
 * Прогрев на простое.
 *
 * Отложенный слот платит за экономию задержкой ПЕРВОГО открытия: пока чанк
 * едет, панель пустая. Поэтому два самых частых слота — репетитор и палитра
 * команд — подтягиваются заранее, когда браузер освободился. Это не то же
 * самое, что стартовый граф: загрузка не блокирует первый экран, не печатает
 * `modulepreload` и не случается у гостя вовсе.
 *
 * `enabled` — признак вошедшего пользователя: гостю на лендинге репетитор
 * недоступен, и качать полтора мегабайта ему незачем.
 */
export function useWarmDeferredGlobals(enabled: boolean): void {
	useEffect(() => {
		if (!enabled) return;
		const warm = (): void => {
			void import("@/components/layout/tutor-side-panel");
			void import("@/components/command-palette/global-command-palette");
		};
		// requestIdleCallback есть не везде (Safari подтянул его поздно) —
		// запасной таймер держит поведение одинаковым на всех платформах.
		const ric = window.requestIdleCallback;
		if (typeof ric === "function") {
			const id = ric(warm, { timeout: 4000 });
			return () => window.cancelIdleCallback?.(id);
		}
		const id = window.setTimeout(warm, 2000);
		return () => window.clearTimeout(id);
	}, [enabled]);
}
